import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from "react";
import classnames from "classnames";
import { Link as ReactLink } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { getButtonClassNames } from "../Button/helpers/getButtonClassNames";
/**
 * Create styled Links using the designs from the Button component.
 * Uses
 * ```jsx
 * <Link to="..."> / <a href="..."/>
 * ```
 * automatically.
 * @param href - Link destination
 * @param target - Link target ("_blank" | "_self" | "_parent" | "_top")
 * @param children - Content to be displayed within the link.
 * @param variant - Style variant of the link. Defaults to "default".
 * @param className - Additional classes to be applied to the link.
 * @returns JSX.Element - Rendered link element.
 *
 * @example
 *
 * ```jsx
 * <Link
    variant={match.isLive ? "primary" : "tertiary"}
    href={match.isLive ? `/live/${broadcastSlugMap?.[match.uuid]}` : `/rewatch/${match.uuid}`}
    >
      {match.isLive ? "Watch Live" : "Rewatch"}
   </Link>
 * ```
 */
export function Link(_a) {
    var { children, variant = "default", className, href, target } = _a, attributes = __rest(_a, ["children", "variant", "className", "href", "target"]);
    const isExternal = useMemo(() => {
        return href.startsWith("http");
    }, [href]);
    const childWrapper = useMemo(() => {
        return _jsx("span", { className: "text-inherit font-style-label-3", children: children });
    }, [children]);
    return isExternal ? (_jsx("a", Object.assign({ className: twMerge(classnames("m-0 flex h-fit w-fit touch-manipulation select-none flex-col place-items-center justify-center rounded-small border border-solid px-7 py-3 no-underline transition-all duration-100 ease-in-out hover:cursor-pointer", getButtonClassNames(variant, false)), className), href: href, target: target }, attributes, { children: childWrapper }))) : (_jsx(ReactLink, Object.assign({ className: twMerge(classnames("m-0 flex h-fit w-fit select-none flex-col place-items-center justify-center rounded-small border border-solid px-7 py-3 no-underline transition-colors duration-[25ms] ease-in-out hover:cursor-pointer", getButtonClassNames(variant, false)), className), to: href, target: target }, attributes, { children: childWrapper })));
}
//# sourceMappingURL=Link.js.map